import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertRailCornerBody, RailCorner, UpdateRailCornerBody } from "./types/railCorners";

export const useRailCornersApi = () => {
  const api = useApi();

  const getAdminRailCorner = useCallback(
    (railCornerId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/rail_corners/${railCornerId}`;
      return useSWRMutation<RailCorner, CustomError, string>(url, (url) => api.get<RailCorner>({ url, signal }));
    },
    [api],
  );

  const useAdminRailCorners = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_corners`, query);
      return useSWRImmutable<Paginated<RailCorner>, CustomError, string>(url, (url) =>
        api.get<Paginated<RailCorner>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminRailCorners = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_corners`, query);
      return api.get<Paginated<RailCorner>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminRailCorner = useCallback(
    async (body: InsertRailCornerBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/rail_corners`;
      const id = await api.post<{ id: string }, InsertRailCornerBody>(url, body);
      mutateCache([url, "/admin/settings/border/rails"]);
      return id;
    },
    [api],
  );

  const updateAdminRailCorner = useCallback(
    async (railCornerId: string, body: UpdateRailCornerBody): Promise<void> => {
      const url = `/admin/settings/border/rail_corners/${railCornerId}`;
      await api.put<UpdateRailCornerBody>(url, body);
      mutateCache(["/admin/settings/border/rail_corners", "/admin/settings/border/rails"]);
    },
    [api],
  );

  const updateAdminRailCornerVisibility = useCallback(
    async (railCornerId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_corners/${railCornerId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/rail_corners", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const updateAdminRailCornerOrder = useCallback(
    async (railCornerId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_corners/${railCornerId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/rail_corners", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const deleteAdminRailCorner = useCallback(
    async (railCorner: RailCorner): Promise<void> => {
      const url = `/admin/settings/border/rail_corners/${railCorner.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/rail_corners`, "/admin/settings/border/rails"]);
    },
    [api],
  );

  return {
    getAdminRailCorner,
    insertAdminRailCorner,
    updateAdminRailCorner,
    updateAdminRailCornerVisibility,
    updateAdminRailCornerOrder,
    deleteAdminRailCorner,
    useAdminRailCorners,
    getAdminRailCorners,
  };
};
